import { SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Button, Input } from '@/components/ui';

export interface AccountsToolbarProps {
    search: string;
    onSearchChange: (value: string) => void;
    /** Present (with a reason) when this context's backend has no `search` param —
     *  see the org adapter: `OrganizationAccountsView` takes no `search` at all. */
    searchDisabledReason?: string;
    activeFilterCount: number;
    onClearFilters: () => void;
    /** "Hiển thị 1–50 / N" — pre-formatted so each adapter can source its own
     *  from/to/total (server page vs. scanned+filtered set). */
    rangeLabel: string;
}

/**
 * EAA-1820: the ONE board toolbar (search box, active-filter chip, range
 * readout) both screens render — replaces `accounts/AccountsFilters.tsx`. The
 * org screen's search box used to be its own inert, disabled-styled markup
 * (`ScopedAccountsHeader`'s `sa-search`); it renders through this same component
 * now, just with `searchDisabledReason` set, so "search does not work here" looks
 * and behaves identically to every other honestly-disabled control in this app
 * rather than being a one-off.
 */
export function AccountsToolbar({
    search,
    onSearchChange,
    searchDisabledReason,
    activeFilterCount,
    onClearFilters,
    rangeLabel,
}: AccountsToolbarProps) {
    const { t } = useTranslation();
    return (
        <div className="acc-toolbar">
            <Input
                className="acc-toolbar__search"
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={t('console.accounts.filters.searchPlaceholder')}
                aria-label={t('console.accounts.filters.searchAriaLabel')}
                prefix={<SearchOutlined />}
                allowClear
                disabled={Boolean(searchDisabledReason)}
                title={searchDisabledReason}
            />

            <span className="acc-toolbar__spacer" />

            {activeFilterCount > 0 && (
                <Button className="acc-toolbar__chip" onClick={onClearFilters}>
                    {t('console.accounts.filters.activeFilterChip', { count: activeFilterCount })}
                </Button>
            )}

            <span className="acc-toolbar__count">{rangeLabel}</span>
        </div>
    );
}

export default AccountsToolbar;
